import { useState, useRef } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Image,
  Upload,
  Loader2,
} from "lucide-react";

export const FileUpload = ({ onUpload, accept = "image/*", multiple = false, label = "Upload Image" }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    
    if (multiple) {
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      try {
        const response = await axios.post(`${API}/admin/upload-multiple`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        onUpload(response.data.uploaded);
        toast.success(`${response.data.count} file(s) uploaded!`);
      } catch (error) {
        toast.error("Failed to upload files");
      }
    } else {
      formData.append('file', files[0]);
      try {
        const response = await axios.post(`${API}/admin/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        onUpload(response.data.url);
        toast.success("File uploaded!");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to upload file");
      }
    }
    
    setUploading(false);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  return (
    <div
      className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
        ${dragActive ? 'border-jade-deep bg-jade-soft/10' : 'border-beige hover:border-champagne-gold'}
        ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
      />
      
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 text-jade-text animate-spin" />
          <span className="font-body text-sm text-stone-grey">Uploading...</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload className="w-8 h-8 text-taupe" />
          <span className="font-body text-sm text-stone-grey">{label}</span>
          <span className="font-body text-xs text-taupe">Drag & drop or click to browse</span>
        </div>
      )}
    </div>
  );
};

